import React, { useEffect } from 'react';
import Form from 'deltoid/core/Form';
import { useForm, ErrorMessage } from 'react-hook-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { websiteUrlRegex } from '@utils/validations';
import ActionCableConsumer from '@components/BaseComponents/ActionCableConsumer';
import AuthManager from '@services/AuthManager';
import { Row, Col } from 'deltoid/core/Blocks/BootstrapContainers';
import { websiteDetailsActions, dialogActions, merchantSelectors, merchantActions, uiActions, toastActions, commentsSelectors, } from '@store/ducks';
import { Small } from 'deltoid/core/Blocks/Typography';
import PayUIcons from "deltoid/core/PayUIcons"
import RadioButton from "../BaseComponents/RadioButton";
import Dialog from '../../components/WebsiteDetails/Dialog';
import { INTEGRATION_TYPE, WEBSITE_APPROVAL_STATUS } from '@constants/websiteDetailsConstants';
import WebsiteCommentForm from '@components/Comments/Website';
import FormFooter from '@components/BaseComponents/FormFooter';

const WebsiteDetails = ({
  integration_type,
  updateMerchantIntegrationType,
  checkLiveliness,
  sendOtp,
  websiteDetails,
  dialogOnClose,
  openWebsiteStatusDialog,
  setLivelinessStatus,
  fetchCurrentWebsiteDetails,
  saveUpdatedWebsiteDetails,
  showToast,
  updateMerchantDetails,
  editable,
  why,
  loadUI,
  getWebsiteComments,
  submitWebsiteComment,
}) => {
  const { handleSubmit, register, errors, setValue, getValues } = useForm({
    mode: 'onBlur',
  });

  useEffect(() => {
    setValue('android_url', websiteDetails.android_url);
    setValue('website_url', websiteDetails.website_url);
    setValue('ios_url', websiteDetails.ios_url);
  }, [websiteDetails.android_url, websiteDetails.website_url, websiteDetails.ios_url]);

  const submitFormData = (formData) => {
    if (editable)
      if (
        !(
          formData.android_url === websiteDetails.android_url &&
          formData.ios_url === websiteDetails.ios_url &&
          formData.website_url === websiteDetails.website_url
        )
      ) {
        saveUpdatedWebsiteDetails(formData);
        // websiteDetails.android_url ||
        // websiteDetails.ios_url ||
        // websiteDetails.website_url
        //   ? sendOtp()
        //   : checkLiveliness();
        checkLiveliness({});
      } else showToast('warning', 'Warning', 'Website Details have not been changed');
  };

  const onSubmit = (formData) => {
    const urlNotUpdated = (
      formData.android_url === websiteDetails.android_url &&
      formData.ios_url === websiteDetails.ios_url &&
      formData.website_url === websiteDetails.website_url
    );
    const { websiteCommentUploadedFile, websiteComment } = getWebsiteComments || {};
    if (websiteDetails.website_approval_status == WEBSITE_APPROVAL_STATUS.ERROR) {
      if ( websiteCommentUploadedFile || websiteComment || !urlNotUpdated) {
        if (!urlNotUpdated) {
          saveUpdatedWebsiteDetails(formData);
        };
        if (editable)
          checkLiveliness({ comment: websiteComment, file: websiteCommentUploadedFile, urlNotUpdated });
      } else {
        showToast('warning', 'Warning', 'Please comment or update app URL')
      }
    } else {
      !formData.android_url && !formData.ios_url && !formData.website_url
        ? showToast('warning', 'Warning', 'Please enter at least one URL')
        : submitFormData(formData);
    }
  };

  const handleLivelinessResponse = (response) => {
    console.log('ws-website: ', response);
    if (response.url_live == true) {
      setLivelinessStatus(response.url_live);
      showToast('success', 'Success', 'Website is live');
      dialogOnClose();
      fetchCurrentWebsiteDetails();
    } else if (response.url_live == false) {
      setLivelinessStatus(response.url_live);
      openWebsiteStatusDialog();
    }
  };

  const handleIntegrationTypeToggle = (event) => {
    if (editable)
      updateMerchantDetails({ integration_type: event.target.value });
  }

  const submitMerchantIntegrationType = () => {
    if (editable)
      updateMerchantIntegrationType(getValues('tools'));
  }

  return (
    <>
      <WebsiteCommentForm />
      <Dialog onClose={dialogOnClose} />
      {AuthManager.getMerchantUUID() && (
        <ActionCableConsumer channelName='WebsiteChannel' identifier={AuthManager.getMerchantUUID()} action={handleLivelinessResponse} />
      )}
      <div className="website-details-wrap">
        <div className="website-confirmation-wrap">
          <Row className='pt-4 px-4'noGutters>
            <Col className='pb-3'>
              <Small variant='medium' className="website-confirmation-text">How do you wish to collect payments?</Small>
              <RadioButton
                className='website-confirmation-radio'
                name="third-party"
                value={INTEGRATION_TYPE.THIRD_PARTY}
                ref={register}
                checked={integration_type === INTEGRATION_TYPE.THIRD_PARTY}
                label='On my Website/App'
                onChange={handleIntegrationTypeToggle}
                disabled={!editable}
              />
              <RadioButton
                name='tools'
                value={INTEGRATION_TYPE.TOOLS}
                ref={register}
                checked={integration_type === INTEGRATION_TYPE.TOOLS}
                label='I don’t have a Website/App'
                onChange={handleIntegrationTypeToggle}
                disabled={!editable}
              />
            </Col>
          </Row>
        </div>
        {
          integration_type === INTEGRATION_TYPE.THIRD_PARTY ? (
            <>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Row className='px-4'>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label id='labelWebsite'>Website</Form.Label>
                      <Form.Text
                        id='website_url'
                        name='website_url'
                        type='text'
                        ref={register({
                          validate: {
                            regex: websiteUrlRegex,
                          },
                        })}
                        placeholder='Enter your website URL'
                        defaultValue={websiteDetails.website_url}
                        autoComplete='off'
                        readOnly={!editable}
                      />
                      <ErrorMessage errors={errors} name='website_url' data-testid='website_url_regex_error'>
                        {({ message }) => <Form.Message type='error'>{message}</Form.Message>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className='px-4'>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label id='labelAndroidApp'>Android app</Form.Label>
                      <Form.Text
                        id='android_url'
                        name='android_url'
                        type='text'
                        ref={register({
                          validate: {
                            regex: websiteUrlRegex,
                          },
                        })}
                        defaultValue={websiteDetails.android_url}
                        placeholder='Enter your app URL'
                        autoComplete='off'
                        readOnly={!editable}
                      />
                      <ErrorMessage errors={errors} name='android_url' data-testid='android_url_regex_error'>
                        {({ message }) => <Form.Message type='error'>{message}</Form.Message>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label id='labelIosApp'>iOS app</Form.Label>
                      <Form.Text
                        id='ios_url'
                        name='ios_url'
                        type='text'
                        ref={register({
                          validate: {
                            regex: websiteUrlRegex,
                          },
                        })}
                        defaultValue={websiteDetails.ios_url}
                        placeholder='Enter your app URL'
                        autoComplete='off'
                        readOnly={!editable}
                      />
                      <ErrorMessage errors={errors} name='ios_url' data-testid='ios_url_regex_error'>
                        {({ message }) => <Form.Message type='error'>{message}</Form.Message>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>
                <p className="website-requirement-text px-4">Important - Your website must have the following pages:</p>
                <ul id="website-requirement-list" className='mx-4'>
                  <li>
                    <a href="#">About Us</a>
                  </li>
                  <li>
                    <a href="#">Terms & Conditions</a>
                  </li>
                  <li>
                    <a href="#">Refund policy</a>
                  </li>
                  <li>
                    <a href="#">Contact Us</a>
                  </li>
                  <li>
                    <a href="#">Privacy Policy</a>
                  </li>
                </ul>
                {
                  editable && (
                    <FormFooter
                      tooltipProps={why}
                      btnProps={{
                        label: websiteDetails.website_approval_status == WEBSITE_APPROVAL_STATUS.ERROR
                          ? 'Submit for Verification'
                          : 'Submit',
                        type: "submit",
                        className: 'website-submit-btn',
                        onClick: handleSubmit(onSubmit),
                      }}
                    />
                  )}
              </form>
            </>
          ) : integration_type === INTEGRATION_TYPE.TOOLS ?
            (
              <>
                <Row noGutters className="odb-payment-links px-4">
                  <p className="website-links-text">You can collect payments with payment tools, this does not requires any integration. </p>
                  <Col md={12}>
                    <ul>
                      {
                        [
                          { label: 'Payment Links', iconType: 'payment-handle'},
                          { label: 'Payment Invoices', iconType: 'invoices'},
                          { label: 'Payment Buttons', iconType: 'shape'},
                        ].map((v) => (
                          <li key={v.label}>
                            <span className="co-icon-box left blue">
                              <PayUIcons
                                type={v.iconType}
                                size="16px"
                              />
                            </span>
                            {v.label}
                          </li>
                        ))
                      }
                    </ul>
                  </Col>
                </Row>
                <p className="website-links-footer px-4">You can submit your website later whenever you wish to use it to collect payments</p>
                {
                  editable && (
                    <FormFooter
                      tooltipProps={why}
                      btnProps={{
                        label: 'Next',
                        type: "submit",
                        className: 'website-submit-btn',
                        onClick: submitMerchantIntegrationType,
                      }}
                    />
                  )}
              </>
            )
            : <></>
        }
      </div>
    </>
  );
};

WebsiteDetails.propTypes = {
  checkLiveliness: PropTypes.func.isRequired,
  sendOtp: PropTypes.func.isRequired,
  websiteDetails: PropTypes.object.isRequired,
  dialogOnClose: PropTypes.func.isRequired,
  openWebsiteStatusDialog: PropTypes.func.isRequired,
  setLivelinessStatus: PropTypes.func.isRequired,
  fetchCurrentWebsiteDetails: PropTypes.func.isRequired,
  saveUpdatedWebsiteDetails: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
};

WebsiteDetails.defaultProps = {
  checkLiveliness: () => { },
  sendOtp: () => { },
  websiteDetails: {},
  dialogOnClose: () => { },
  openWebsiteStatusDialog: () => { },
  setLivelinessStatus: () => { },
  fetchCurrentWebsiteDetails: () => { },
  saveUpdatedWebsiteDetails: () => { },
  showToast: () => { },
};

const mapDispatchToProps = (dispatch) => ({
  saveUpdatedWebsiteDetails: (updatedWebsiteDetails) => dispatch(websiteDetailsActions.saveUpdatedWebsiteDetails(updatedWebsiteDetails)),
  checkLiveliness: ({comment, file, urlNotUpdated}) => dispatch(websiteDetailsActions.checkWebsiteLiveliness({comment, file, urlNotUpdated})),
  dialogOnClose: () => dispatch(dialogActions.close()),
  sendOtp: () => dispatch(websiteDetailsActions.sendOtp()),
  openWebsiteStatusDialog: () => dispatch(dialogActions.open('website-status')),
  setLivelinessStatus: (url_live) => dispatch(websiteDetailsActions.setLivelinessStatus(url_live)),
  fetchCurrentWebsiteDetails: () => dispatch(websiteDetailsActions.fetchCurrentWebsiteDetails()),
  showToast: (variant, title, content) => dispatch(toastActions.show({ variant: variant, title: title, content: content })),
  updateMerchantIntegrationType: (integration_type) => dispatch(websiteDetailsActions.updateMerchantIntegrationType(integration_type)),
  updateMerchantDetails: (merchantDetails) => dispatch(merchantActions.updateMerchantDetails(merchantDetails)),
  loadUI: (details) => dispatch(uiActions.loadUI(details)),
  submitWebsiteComment: (comment) => dispatch(commentsAction.submitWebsiteComment(comment)),
});

const mapStateToProps = (state) => ({
  liveliness_status: state.onboardingApp.websiteDetails.liveliness_status,
  websiteDetails: merchantSelectors.getWebsiteDetails(state),
  integration_type: merchantSelectors.getMerchantIntegrationType(state),
  getWebsiteComments: commentsSelectors.getWebsiteComments(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(WebsiteDetails);
